import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export type ServiceType =
  | "ocean-freight"
  | "air-freight"
  | "customs"
  | "warehousing"
  | "intermodal"
  | "project-cargo"
  | "insurance"
  | "supply-chain"
  | "general";

interface ServiceQuoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceType: ServiceType;
}

const serviceConfig: Record<ServiceType, { title: string; description: string }> = {
  "ocean-freight": {
    title: "Ocean Freight Quote Request",
    description: "Get a competitive quote for FCL, LCL, or breakbulk ocean shipments"
  },
  "air-freight": {
    title: "Air Freight Quote Request",
    description: "Request rates for express, standard, or charter air cargo services"
  },
  "customs": {
    title: "Customs Brokerage Quote",
    description: "Get a quote for import/export customs clearance services"
  },
  "warehousing": {
    title: "Warehousing Quote Request",
    description: "Request pricing for storage, distribution, and fulfillment services"
  },
  "intermodal": {
    title: "Intermodal Transport Quote",
    description: "Get rates for combined sea, rail, and road transport solutions"
  },
  "project-cargo": {
    title: "Project Cargo Quote",
    description: "Request a custom quote for oversized, heavy lift, or complex shipments"
  },
  "insurance": {
    title: "Cargo Insurance Quote",
    description: "Get coverage options and rates for your shipment protection"
  },
  "supply-chain": {
    title: "Supply Chain Consulting",
    description: "Request a consultation for supply chain optimization"
  },
  "general": {
    title: "Request a Quote",
    description: "Tell us about your shipping needs and we'll provide a tailored solution"
  }
};

export const ServiceQuoteModal = ({ open, onOpenChange, serviceType }: ServiceQuoteModalProps) => {
  const config = serviceConfig[serviceType];

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    origin: "",
    destination: "",
    cargoDescription: "",
    weight: "",
    dimensions: "",
    preferredDate: "",
    message: "",
    // Ocean specific
    containerType: "",
    incoterms: "",
    // Air specific
    urgency: "",
    // Customs specific
    hsCode: "",
    importExport: "",
    // Warehousing specific
    storageType: "",
    duration: "",
    palletCount: "",
    // Project cargo specific
    cargoWeight: "",
    specialRequirements: [] as string[],
    // Insurance specific
    cargoValue: "",
    coverageType: "",
    // Supply chain specific
    consultationType: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formType: 'service-quote',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: config.title,
          details: `
Origin: ${formData.origin}
Destination: ${formData.destination}
Cargo: ${formData.cargoDescription}
Weight: ${formData.weight || formData.cargoWeight}
Dimensions: ${formData.dimensions}
Preferred Date: ${formData.preferredDate}
Service Specifics:
${formData.containerType ? `Container: ${formData.containerType}` : ''}
${formData.incoterms ? `Incoterms: ${formData.incoterms}` : ''}
${formData.urgency ? `Urgency: ${formData.urgency}` : ''}
${formData.hsCode ? `HS Code: ${formData.hsCode}` : ''}
${formData.importExport ? `Import/Export: ${formData.importExport}` : ''}
${formData.storageType ? `Storage: ${formData.storageType}` : ''}
${formData.duration ? `Duration: ${formData.duration}` : ''}
${formData.palletCount ? `Pallets/Volume: ${formData.palletCount}` : ''}
${formData.cargoValue ? `Value: ${formData.cargoValue}` : ''}
${formData.coverageType ? `Coverage: ${formData.coverageType}` : ''}
${formData.consultationType ? `Consultation: ${formData.consultationType}` : ''}
${formData.specialRequirements.length > 0 ? `Special: ${formData.specialRequirements.join(', ')}` : ''}
Additional Message: ${formData.message}
          `,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Quote request submitted! Our specialist team will contact you within 24 hours.");
        onOpenChange(false);
        setFormData({
          name: "", company: "", email: "", phone: "", origin: "", destination: "",
          cargoDescription: "", weight: "", dimensions: "", preferredDate: "", message: "",
          containerType: "", incoterms: "", urgency: "", hsCode: "", importExport: "",
          storageType: "", duration: "", palletCount: "", cargoWeight: "",
          specialRequirements: [], cargoValue: "", coverageType: "", consultationType: "",
        });
      } else {
        toast.error(data.error || 'Failed to submit request. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to submit request. Please email us directly at support@costalmeridian.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderServiceFields = () => {
    switch (serviceType) {
      case "ocean-freight":
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Container Type *</Label>
                <Select value={formData.containerType} onValueChange={(v) => setFormData({ ...formData, containerType: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fcl-20">20' FCL</SelectItem>
                    <SelectItem value="fcl-40">40' FCL</SelectItem>
                    <SelectItem value="fcl-40hc">40' High Cube</SelectItem>
                    <SelectItem value="lcl">LCL (Groupage)</SelectItem>
                    <SelectItem value="reefer">Reefer Container</SelectItem>
                    <SelectItem value="open-top">Open Top</SelectItem>
                    <SelectItem value="flat-rack">Flat Rack</SelectItem>
                    <SelectItem value="breakbulk">Breakbulk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Incoterms</Label>
                <Select value={formData.incoterms} onValueChange={(v) => setFormData({ ...formData, incoterms: v })}>
                  <SelectTrigger><SelectValue placeholder="Select terms" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exw">EXW - Ex Works</SelectItem>
                    <SelectItem value="fob">FOB - Free on Board</SelectItem>
                    <SelectItem value="cif">CIF - Cost, Insurance, Freight</SelectItem>
                    <SelectItem value="cfr">CFR - Cost and Freight</SelectItem>
                    <SelectItem value="dap">DAP - Delivered at Place</SelectItem>
                    <SelectItem value="ddp">DDP - Delivered Duty Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        );

      case "air-freight":
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Urgency Level *</Label>
                <Select value={formData.urgency} onValueChange={(v) => setFormData({ ...formData, urgency: v })}>
                  <SelectTrigger><SelectValue placeholder="Select urgency" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="express">Express (24-48 hrs)</SelectItem>
                    <SelectItem value="priority">Priority (3-5 days)</SelectItem>
                    <SelectItem value="standard">Standard (5-7 days)</SelectItem>
                    <SelectItem value="economy">Economy (7-10 days)</SelectItem>
                    <SelectItem value="charter">Charter (On demand)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Weight (kg) *</Label>
                <Input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  placeholder="Total weight in kg"
                />
              </div>
            </div>
            <div>
              <Label>Dimensions (L x W x H cm)</Label>
              <Input
                value={formData.dimensions}
                onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                placeholder="e.g., 120 x 80 x 100"
              />
            </div>
          </>
        );

      case "customs":
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Import or Export *</Label>
                <Select value={formData.importExport} onValueChange={(v) => setFormData({ ...formData, importExport: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="import">Import Clearance</SelectItem>
                    <SelectItem value="export">Export Clearance</SelectItem>
                    <SelectItem value="transit">Transit Documentation</SelectItem>
                    <SelectItem value="temporary">Temporary Import/Export</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>HS Code (if known)</Label>
                <Input
                  value={formData.hsCode}
                  onChange={(e) => setFormData({ ...formData, hsCode: e.target.value })}
                  placeholder="e.g., 8471.30.00"
                />
              </div>
            </div>
          </>
        );

      case "warehousing":
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Storage Type *</Label>
                <Select value={formData.storageType} onValueChange={(v) => setFormData({ ...formData, storageType: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Warehouse</SelectItem>
                    <SelectItem value="bonded">Bonded Warehouse</SelectItem>
                    <SelectItem value="temperature">Temperature Controlled</SelectItem>
                    <SelectItem value="hazmat">Hazardous Materials</SelectItem>
                    <SelectItem value="ecommerce">E-commerce Fulfillment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expected Duration *</Label>
                <Select value={formData.duration} onValueChange={(v) => setFormData({ ...formData, duration: v })}>
                  <SelectTrigger><SelectValue placeholder="Select duration" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Short-term (&lt; 1 month)</SelectItem>
                    <SelectItem value="medium">Medium (1-6 months)</SelectItem>
                    <SelectItem value="long">Long-term (6+ months)</SelectItem>
                    <SelectItem value="ongoing">Ongoing/Permanent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Estimated Pallet Positions / Volume</Label>
              <Input
                value={formData.palletCount}
                onChange={(e) => setFormData({ ...formData, palletCount: e.target.value })}
                placeholder="e.g., 50 pallets or 100 CBM"
              />
            </div>
          </>
        );

      case "intermodal":
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Container Type *</Label>
                <Select value={formData.containerType} onValueChange={(v) => setFormData({ ...formData, containerType: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20ft">20' Container</SelectItem>
                    <SelectItem value="40ft">40' Container</SelectItem>
                    <SelectItem value="40hc">40' High Cube</SelectItem>
                    <SelectItem value="45hc">45' High Cube</SelectItem>
                    <SelectItem value="swap-body">Swap Body</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred Modes</Label>
                <Select value={formData.incoterms} onValueChange={(v) => setFormData({ ...formData, incoterms: v })}>
                  <SelectTrigger><SelectValue placeholder="Select modes" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sea-rail">Sea + Rail</SelectItem>
                    <SelectItem value="sea-road">Sea + Road</SelectItem>
                    <SelectItem value="rail-road">Rail + Road</SelectItem>
                    <SelectItem value="all">All Modes (Best Route)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        );

      case "project-cargo":
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Total Cargo Weight *</Label>
                <Input
                  value={formData.cargoWeight}
                  onChange={(e) => setFormData({ ...formData, cargoWeight: e.target.value })}
                  placeholder="e.g., 50 tonnes"
                />
              </div>
              <div>
                <Label>Dimensions (L x W x H)</Label>
                <Input
                  value={formData.dimensions}
                  onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                  placeholder="e.g., 15m x 4m x 3.5m"
                />
              </div>
            </div>
            <div>
              <Label>Special Requirements</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {["Heavy Lift Crane", "Route Survey", "Permits & Escorts", "Assembly/Disassembly", "Civil Works", "Customs Expertise"].map((req) => (
                  <div key={req} className="flex items-center space-x-2">
                    <Checkbox
                      id={req}
                      checked={formData.specialRequirements.includes(req)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, specialRequirements: [...formData.specialRequirements, req] });
                        } else {
                          setFormData({ ...formData, specialRequirements: formData.specialRequirements.filter(r => r !== req) });
                        }
                      }}
                    />
                    <Label htmlFor={req} className="text-sm cursor-pointer">{req}</Label>
                  </div>
                ))}
              </div>
            </div>
          </>
        );

      case "insurance":
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Cargo Value (EUR) *</Label>
                <Input
                  type="number"
                  value={formData.cargoValue}
                  onChange={(e) => setFormData({ ...formData, cargoValue: e.target.value })}
                  placeholder="Total cargo value"
                />
              </div>
              <div>
                <Label>Coverage Type *</Label>
                <Select value={formData.coverageType} onValueChange={(v) => setFormData({ ...formData, coverageType: v })}>
                  <SelectTrigger><SelectValue placeholder="Select coverage" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-risk">All-Risk Coverage</SelectItem>
                    <SelectItem value="named-perils">Named Perils</SelectItem>
                    <SelectItem value="total-loss">Total Loss Only</SelectItem>
                    <SelectItem value="war-strikes">War & Strikes Extension</SelectItem>
                    <SelectItem value="annual">Annual Open Policy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        );

      case "supply-chain":
        return (
          <div>
            <Label>Consultation Type *</Label>
            <Select value={formData.consultationType} onValueChange={(v) => setFormData({ ...formData, consultationType: v })}>
              <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="optimization">Supply Chain Optimization</SelectItem>
                <SelectItem value="network">Network Design</SelectItem>
                <SelectItem value="cost-reduction">Cost Reduction Analysis</SelectItem>
                <SelectItem value="technology">Technology Integration</SelectItem>
                <SelectItem value="risk">Risk Assessment</SelectItem>
                <SelectItem value="sustainability">Sustainability Strategy</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Common Contact Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="company">Company Name</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Your Company Ltd"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@company.com"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+30 210 123 4567"
              />
            </div>
          </div>

          {/* Origin & Destination (for shipping services) */}
          {["ocean-freight", "air-freight", "intermodal", "project-cargo", "insurance"].includes(serviceType) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="origin">Origin *</Label>
                <Input
                  id="origin"
                  required
                  value={formData.origin}
                  onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                  placeholder="City, Country"
                />
              </div>
              <div>
                <Label htmlFor="destination">Destination *</Label>
                <Input
                  id="destination"
                  required
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  placeholder="City, Country"
                />
              </div>
            </div>
          )}

          {/* Service-Specific Fields */}
          {renderServiceFields()}

          {/* Cargo Description */}
          <div>
            <Label htmlFor="cargoDescription">Cargo Description *</Label>
            <Textarea
              id="cargoDescription"
              required
              value={formData.cargoDescription}
              onChange={(e) => setFormData({ ...formData, cargoDescription: e.target.value })}
              placeholder="Describe your cargo, commodity type, and any special handling requirements..."
              rows={3}
            />
          </div>

          {/* Preferred Date */}
          <div>
            <Label htmlFor="preferredDate">Preferred Date</Label>
            <Input
              id="preferredDate"
              type="date"
              value={formData.preferredDate}
              onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
            />
          </div>

          {/* Additional Message */}
          <div>
            <Label htmlFor="message">Additional Information</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Any other details or questions..."
              rows={2}
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
